"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Card, CardBody, Input, Badge } from "reactstrap"
import DataTable from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import ExhibitionService from "../../../Service/ExhibitionService"
import type { Exhibition } from "./types"
import { FaPlus, FaEdit, FaTrash, FaEye } from "react-icons/fa"
import { toast } from "react-toastify"

interface Props {
  onAdd: () => void
  onEdit: (item: Exhibition) => void
  refresh: boolean
}

const ExhibitionList: React.FC<Props> = ({ onAdd, onEdit, refresh }) => {
  const [data, setData] = useState<Exhibition[]>([])
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalRows, setTotalRows] = useState(0)
  const [loading, setLoading] = useState(false)
 
  const navigate = useNavigate()

  const fetchData = async () => {
    try {
      const response = await ExhibitionService.getAll()
      setData(response.results)
    } catch (err) {
      console.error("Failed to fetch exhibition:", err)
    }
  }

  useEffect(() => {
    fetchData()
  }, [page, perPage, search, refresh])

  const handleDelete = async (id: number) => {
    try {
      await ExhibitionService.delete(id)
      toast.success("Exhibition deleted")
      fetchData()
    } catch {
      toast.error("Failed to delete exhibition")
    }
  }

  const handleViewDetails = (exhibition: Exhibition) => {
    navigate(`${process.env.PUBLIC_URL}/exhibition/${exhibition.id}/related`)
  }

  const filteredData = data.filter((item) => {
  const searchTerm = search.toLowerCase()

  return (
    item.title?.toLowerCase().includes(searchTerm) ||
    item.venue?.toLowerCase().includes(searchTerm) ||
    item.start_date?.toLowerCase().includes(searchTerm) ||
    item.end_date?.toLowerCase().includes(searchTerm) ||
    (item.status ? "active" : "inactive").includes(searchTerm)
  )
})


  const columns = [
    {
      name: "Title",
      selector: (row: Exhibition) => row.title,
      sortable: true,
    },
    {
      name: "Venue",
      selector: (row: Exhibition) => row.venue,
      sortable: true,
    },
    {
      name: "Duration",
      selector: (row: Exhibition) => `${row.start_date} to ${row.end_date}`,
      sortable: true,
    },
    {
      name: "Status",
      cell: (row: Exhibition) => (
        <Badge color={row.status ? "success" : "secondary"}>{row.status ? "Active" : "Inactive"}</Badge>
      ),
    },
    {
      name: "Actions",
      cell: (row: Exhibition) => (
        <div className="d-flex gap-1">
          <Btn color="success" size="xs" onClick={() => handleViewDetails(row)} title="View Details">
            <FaEye size={12} />
          </Btn>
          <Btn color="info" size="xs" onClick={() => onEdit(row)} title="Edit">
            <FaEdit size={12} />
          </Btn>
          <Btn color="danger" size="xs" onClick={() => handleDelete(row.id)} title="Delete">
            <FaTrash size={12} />
          </Btn>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ]

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    setPage(page)
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Input
                    type="search"
                    placeholder="Search exhibition ..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 300 }}
                  />
          <Btn color="primary" size="sm" onClick={onAdd}>
            <FaPlus className="me-1" size={12} /> Add Exhibition
          </Btn>
        </div>

        <DataTable
          columns={columns}
          data={filteredData}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          striped
          highlightOnHover
          responsive
          noDataComponent="No exhibitions found."
        />
      </CardBody>
    </Card>
  )
}

export default ExhibitionList
